'use client';
import { useSearchParams, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { TextInput } from '../../Shared/Form/TextInput';
import Button from '../../Shared/Form/Button';
import { Alert } from 'antd';
import { useFormik } from 'formik';
import { loginSchema } from './Schema';
import { LoginPayload } from '@/types/Auth';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import nextApi from '@/utils/axios/nextApi';
import Link from 'next/link';
import { routes } from '@/utils/routes';
import { showFormError } from '@/utils/common';
import { Color } from '@/utils/constants';
import { getSession, useSession } from 'next-auth/react';
import { At, Key } from '@phosphor-icons/react';
import { AuthFormWrapper } from '../AuthFormWrapper';
import { TermsAndConditions } from './TermsAndConditions';
import { User } from '@/types/User';

export default function LoginForm() {
  const searchParams = useSearchParams();
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState('');
  const router = useRouter();
  const { status } = useSession();
  const [loading, setLoading] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: async (values: LoginPayload) => {
      return await nextApi.post<{ user: User }>('/login', {
        ...values,
        type: 'credentials',
      });
    },
    onSuccess: async () => {
      const session = await getSession();

      // Write the token immediately so api.ts interceptor can read it
      // before AuthProvider's useEffect fires on the next render cycle.
      if (session?.user?.accessToken) {
        localStorage.setItem('token', session.user.accessToken);
      }

      if (!session?.user?.termsAcceptedAt) {
        setIsTermsModalOpen(true);
      } else {
        setAuthenticated(true);
      }
    },
    onError: (error) => {
      setLoading(false);

      if (axios.isAxiosError(error)) {
        const apiError =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Something went wrong';
        setError(apiError);
      } else {
        setError('Something went wrong');
      }
    },
  });

  const { isPending } = mutation;

  const formik = useFormik<LoginPayload>({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginSchema,
    onSubmit: (values) => {
      mutation.mutate(values);
    },
  });

  useEffect(() => {
    if (authenticated) {
      const next = searchParams.get('next') ?? routes.dashboard;
      window.location.href = next;
    }
  }, [authenticated, router, searchParams]);

  useEffect(() => {
    if (status === 'loading' || isPending) {
      setLoading(true);
    }
  }, [status, isPending]);

  return (
    <AuthFormWrapper title="Login into your account">
      <div className="text-[14px] text-primary py-4">
        Input your login details
      </div>
      <form onSubmit={formik.handleSubmit} className="space-y-3" method="POST">
        <div className="w-full">
          <div>
            {error && (
              <Alert message={error} type="error" className="my-4 text-sm" />
            )}
          </div>
          <div>
            <div className="relative">
              <TextInput
                id="email"
                name="email"
                onChange={formik.handleChange}
                placeholder="example@gmail.com"
                prefix={<At fontSize={18} color={Color.Primary} />}
                required
                error={showFormError(formik.errors, formik.touched, 'email')}
              />
            </div>
          </div>
          <div className="mt-4">
            <div className="relative">
              <TextInput
                id="password"
                type="password"
                name="password"
                onChange={formik.handleChange}
                placeholder="Enter your password"
                prefix={<Key fontSize={18} color={Color.Primary} />}
                required
                error={showFormError(formik.errors, formik.touched, 'password')}
              />
            </div>
          </div>
        </div>
        <Button
          disabled={loading}
          className="mt-4 w-full"
          loading={mutation.isPending || status === 'loading'}
        >
          Sign In
        </Button>
        <div className="text-primary capitalize text-sm">
          <Link href={routes.forgotPassword}>Forgot your password?</Link>
        </div>
        <div
          className="flex h-8 items-end space-x-1"
          aria-live="polite"
          aria-atomic="true"
        ></div>
      </form>

      <TermsAndConditions isOpen={isTermsModalOpen} />
    </AuthFormWrapper>
  );
}
